import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCheck,
  faXmark,
  faRotateLeft,
  faShare,
  faComments,
  faStamp,
} from '@fortawesome/free-solid-svg-icons';
import {
  authorizationCaseService,
  CUSTOMER_RESPONSE_LABELS,
  COMMUNICATION_METHOD_LABELS,
  type AuthorizationCommunicationMethod,
  type CustomerResponseType,
} from '@/services/authorization-case.service';
import { authorizationQuotationService } from '@/services/authorization-quotation.service';
import { inspectionService } from '@/services/inspection.service';
import { procedureExecutionService } from '@/features/inspection/services/procedure-execution.service';
import { customerService } from '@/services/customer.service';
import { SkeletonPage } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useHasPermission } from '@/hooks/use-permissions';
import { useAuthStore } from '@/stores/auth.store';
import { toast, swalConfirm } from '@/lib/swal';
import { ROUTES } from '@/routes/routes';
import { CaseSummaryCard } from './components/CaseSummaryCard';
import { WhyApprovalRequiredPanel } from './components/WhyApprovalRequiredPanel';
import { InspectionContextPanel } from './components/InspectionContextPanel';
import { EngineerJustificationPanel } from './components/EngineerJustificationPanel';
import { EvidenceGallery } from './components/EvidenceGallery';
import { FindingContextPanel } from './components/FindingContextPanel';
import { CaseStatusPanel } from './components/CaseStatusPanel';
import { AuthorizationRadar } from './components/AuthorizationRadar';
import { ExpectedRoutePanel } from './components/ExpectedRoutePanel';
import { QuotationPanel } from './components/QuotationPanel';
import {
  AuthorizationSectionNav,
  SECTION_SCROLL_MARGIN,
  type AuthorizationSection,
} from './components/AuthorizationSectionNav';

/**
 * Anchor target ids for the sections this page really renders.
 *
 * Naming follows the ids already present on this screen —
 * `expected-route-heading` (ExpectedRoutePanel) and `authorization-radar-heading`
 * (AuthorizationRadar) — i.e. kebab-case, `authorization-` prefixed where the
 * subject is the case itself. These identify the section CONTAINER, so they are
 * not suffixed `-heading` and cannot collide with those two existing heading ids.
 *
 * Every entry below corresponds to a panel that is unconditionally rendered by
 * this page, except `expectedRoute` (ExpectedRoutePanel returns null without a
 * governed route) and `radar` (mounted only when the route param resolved) —
 * both of which are filtered out of the anchor list when absent.
 */
const SECTION_IDS = {
  expectedRoute: 'authorization-expected-route',
  summary: 'authorization-summary',
  whyApproval: 'authorization-why-approval',
  inspection: 'authorization-inspection',
  justification: 'authorization-justification',
  evidence: 'authorization-evidence',
  findings: 'authorization-findings',
  // Phase 7 Slice 5B — present only for request kinds that carry a quotation
  // (the anchor is filtered out with the panel, like `expectedRoute` / `radar`).
  quotation: 'authorization-quotation',
  radar: 'authorization-radar',
  timeline: 'authorization-timeline',
} as const;

const SELECT_CLASS =
  'flex h-10 w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50';

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

/**
 * Stage 5E · **§22 #6 = REPLACE** — `CaseProgress` was removed here.
 *
 * It rendered a HARDCODED pipeline (`Created → OPS Review → {Commercial|Admin}
 * Review → OPS Release → Engineer Resumed`) and greyed out the stages after a
 * computed "current" index. Those greyed-out stages were **predicted future
 * state**, which **D-112 OD-R1** forbids outright — *"No predicted or future
 * stage may be rendered, including greyed-out 'upcoming' nodes"* — and which
 * **D-113 §5** explicitly declines to validate.
 *
 * Its replacement is `ExpectedRoutePanel`, fed by the GOVERNED expected-route
 * comparison: the case's own immutable published route version (D-119 §4, bound
 * at creation by 5C) compared against its actual journey (5D, derived on read).
 *
 * **No CaseProgress state, model, table or permission was created**, and the UI
 * computes no route state of its own — there is exactly one expected-route
 * source of truth, and it is the backend's.
 *
 * The one piece of the old helper worth keeping was its RETURNED_TO_ENGINEER
 * banner: that is PRESENT state, not prediction, so it survives below as a plain
 * status notice rather than as a stage in a pipeline.
 */

export function AuthorizationCaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const can = useHasPermission();
  const user = useAuthStore((st) => st.user);

  const [comment, setComment] = useState('');
  const [commMethod, setCommMethod] = useState<AuthorizationCommunicationMethod>('PORTAL');
  const [commContactId, setCommContactId] = useState('');
  const [commSummary, setCommSummary] = useState('');
  const [commDecision, setCommDecision] = useState<CustomerResponseType | ''>('');
  const [expandedEvidenceEntryId, setExpandedEvidenceEntryId] = useState<string | null>(null);

  const { data: kase, isLoading } = useQuery({
    queryKey: ['authorization-case', id],
    queryFn: () => authorizationCaseService.get(id!),
    enabled: !!id,
  });

  // Stage 5E — the SAME query key the radar uses, so this is one shared request,
  // not a second fetch, and the expected and actual layers read one response.
  const { data: journey } = useQuery({
    queryKey: ['authorization-case-journey', id],
    queryFn: () => authorizationCaseService.getJourney(id!),
    enabled: !!id,
  });

  /**
   * Phase 7 Slice 5B — the request-scoped quotation thread (D-127 OD-5; design
   * §J). One read per case; the server says whether the kind carries a
   * quotation (`requiresQuotation`) and the panel renders nothing otherwise.
   * The same query key is written back by the panel after each of its own
   * writes, so the acting user never sees a stale thread.
   */
  const {
    data: quotation,
    isLoading: quotationLoading,
    isError: quotationError,
    refetch: refetchQuotation,
  } = useQuery({
    queryKey: ['authorization-quotation', id],
    queryFn: () => authorizationQuotationService.getThread(id!),
    enabled: !!id && !!kase,
  });

  const { data: inspection } = useQuery({
    queryKey: ['inspection', kase?.inspectionId],
    queryFn: () => inspectionService.get(kase!.inspectionId),
    enabled: !!kase?.inspectionId,
  });

  const { data: contacts } = useQuery({
    queryKey: ['customer-contacts', inspection?.customerId],
    queryFn: () => customerService.listContacts(inspection!.customerId!),
    enabled: !!inspection?.customerId,
  });

  // Context enhancement — reuses the same execution/findings reads already used by
  // ProcedureExecutionPanel and the Finding accordion, just to locate the one step/
  // finding this case is about (docs/reports/authorization context-enhancement task).
  const { data: execution } = useQuery({
    queryKey: ['procedure-execution', kase?.inspectionId],
    queryFn: () => procedureExecutionService.getExecution(kase!.inspectionId),
    enabled: !!kase?.inspectionId && !!kase?.procedureStepExecutionId,
  });

  const { data: findingsList } = useQuery({
    queryKey: ['inspection-findings', kase?.inspectionId],
    queryFn: () => inspectionService.getFindings(kase!.inspectionId, { pageSize: 100 }),
    enabled: !!kase?.inspectionId && !!kase?.findingId,
  });

  const step = useMemo(() => {
    if (!execution || !kase?.procedureStepExecutionId) return null;
    return (
      execution.sections
        .flatMap((s) => s.steps)
        .find((s) => s.stepExecutionId === kase.procedureStepExecutionId) ?? null
    );
  }, [execution, kase?.procedureStepExecutionId]);

  const finding = useMemo(() => {
    if (!findingsList || !kase?.findingId) return null;
    return findingsList.data.find((f) => f.id === kase.findingId) ?? null;
  }, [findingsList, kase?.findingId]);

  const engineerRole = useMemo(
    () => kase?.history.find((h) => h.action === 'REQUESTED')?.actorRoleName ?? null,
    [kase],
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['authorization-case', id] });
    queryClient.invalidateQueries({ queryKey: ['authorization-cases'] });
    // D-112 Stage 4. The radar's journey is a separate query key, so without
    // this line the operator's OWN action refreshed the Case Status panel while
    // the journey beneath it still showed the pre-action trail — a stale
    // journey presented next to fresh live state, which is exactly the kind of
    // untruth §16 exists to prevent.
    //
    // Deliberately NOT left to the realtime signal. That path is correct but
    // asynchronous and best-effort (worker + Redis pub/sub, D-018), so the actor
    // would watch their own change appear late or, on a lost publish, not at
    // all. Realtime keeps OTHER operators' screens current; this keeps the
    // acting operator's own screen honest.
    queryClient.invalidateQueries({ queryKey: ['authorization-case-journey', id] });
    // Slice 5B — a customer decision recorded through "Send to Customer" moves
    // the SENT revision too, so the thread is refreshed with the case.
    queryClient.invalidateQueries({ queryKey: ['authorization-quotation', id] });
  };

  const respondMutation = useMutation({
    mutationFn: async (action: 'APPROVED' | 'REJECTED' | 'RETURNED'): Promise<void> => {
      if (!kase) throw new Error('Case not loaded');
      if (kase.findingId) {
        await inspectionService.respondAuthorization(kase.inspectionId, kase.findingId, kase.id, {
          action,
          comment: comment || undefined,
        });
        return;
      }
      await procedureExecutionService.respondStepAuthorization(
        kase.inspectionId,
        kase.procedureStepExecutionId!,
        kase.id,
        { action, comment: comment || undefined },
      );
    },
    onSuccess: () => {
      toast.success('Decision recorded');
      setComment('');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to record decision')),
  });

  const forwardMutation = useMutation({
    mutationFn: (target: 'COMMERCIAL' | 'ADMIN') =>
      target === 'COMMERCIAL'
        ? authorizationCaseService.forwardToCommercial(id!, comment || undefined)
        : authorizationCaseService.forwardToAdmin(id!, comment || undefined),
    onSuccess: () => {
      toast.success('Case forwarded');
      setComment('');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to forward case')),
  });

  const unclaimMutation = useMutation({
    mutationFn: () => authorizationCaseService.unclaim(id!),
    onSuccess: () => {
      toast.success('Claim released — the case is back in the intake pool');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to release claim')),
  });

  const claimMutation = useMutation({
    mutationFn: () => authorizationCaseService.claim(id!),
    onSuccess: () => {
      toast.success('Case claimed — it is now assigned to you');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to claim case')),
  });

  const releaseMutation = useMutation({
    mutationFn: (action: 'APPROVED' | 'REJECTED') =>
      authorizationCaseService.release(id!, { action, comment: comment || undefined }),
    onSuccess: () => {
      toast.success('Case released to engineer');
      setComment('');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to release case')),
  });

  /**
   * Phase 7 Slice 1 (D-127 OD-1 / design §2.5 W-4) — the EXPLICIT hand-back.
   *
   * Commercial may return a case to Operations WITHOUT a recommendation. The
   * backend capability (`PATCH /authorization-cases/:id/return-to-ops`) has
   * existed since the Case Management implementation but had no UI caller, so
   * Commercial could only hand back by approving or rejecting. This wires the
   * existing endpoint; it adds no new mechanism. The server still applies
   * `assertActorOwnsCase` + `assertActorIsAssignee` and its own stage guard.
   */
  const returnToOpsMutation = useMutation({
    mutationFn: () => authorizationCaseService.returnToOps(id!, comment || undefined),
    onSuccess: () => {
      toast.success('Case returned to Operations — it is back in the OPS intake pool');
      setComment('');
      invalidate();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to return case to Operations')),
  });

  /**
   * Phase 7 Slice 1 (D-127 OD-2 / design §2.5 W-5) — "Send to Customer".
   *
   * The frozen business workflow names the customer step "Send to Customer".
   * It is an OWNERSHIP-PRESERVING waiting state: the communication row sets
   * `waitingFor = CUSTOMER` and nothing else — `currentOwner`, `assignedUserId`,
   * `workflowStage` and `status` are untouched, and the department that sent it
   * remains the owner while the customer decides. The same form, with a
   * Decision selected, records the customer's response on their behalf and
   * flips `waitingFor` back to the owning department. Both are the one existing
   * `recordCommunication` endpoint; only the presentation changed.
   */
  const communicationMutation = useMutation({
    mutationFn: () =>
      authorizationCaseService.recordCommunication(id!, {
        method: commMethod,
        customerContactId: commContactId || undefined,
        summary: commSummary,
        decision: commDecision || undefined,
      }),
    onSuccess: () => {
      toast.success(
        commDecision
          ? 'Customer response recorded'
          : 'Sent to customer — the case stays with your department while they decide',
      );
      setCommSummary('');
      setCommDecision('');
      invalidate();
    },
    onError: (e: Error) =>
      toast.error(
        extractMsg(
          e,
          commDecision ? 'Failed to record customer response' : 'Failed to send to customer',
        ),
      ),
  });

  if (isLoading || !kase) return <SkeletonPage />;

  /**
   * Private Workflow Visibility — the UI must not offer an action the backend
   * will refuse. `assertActorOwnsCase` requires the actor's role class to equal
   * the case's `currentOwner`, so the same condition is applied here.
   *
   * Before this, `canRespond` checked only the permission and PENDING status,
   * which showed Approve/Reject to a Commercial or Admin user looking at a case
   * still sitting with OPS. `canForward` and `canRelease` alongside it already
   * checked ownership; this closes the inconsistency. The backend remains
   * authoritative — this only stops presenting an action that cannot succeed.
   */
  const actorOwnerClass =
    user?.roleSlug === 'operations-user'
      ? 'OPS'
      : user?.roleSlug === 'commercial-user'
        ? 'COMMERCIAL'
        : user?.roleSlug === 'admin'
          ? 'ADMIN'
          : null;
  const isCurrentOwner = actorOwnerClass !== null && actorOwnerClass === kase.currentOwner;
  const isOwnerRole = user?.roleSlug === 'owner';

  /**
   * CLAIM-FIRST + ASSIGNEE-ONLY (D-2 / D-5) — the UI mirrors the server guard
   * exactly, so no control is ever offered that the backend would refuse.
   *
   *   unclaimed        → Claim only; no decision actions
   *   claimed by me    → the decision actions, plus Unclaim
   *   claimed by a peer→ nothing (and the case is normally not even visible)
   *
   * Owner keeps the emergency override, matching the server's wildcard bypass.
   */
  const isUnclaimed = !kase.assignedUserId;
  const isAssignee = kase.assignedUserId === user?.userId;

  /**
   * Only OPS and COMMERCIAL use private per-user assignment — ADMIN does not,
   * and must not be made to claim before acting (that was the R-1 regression).
   * This mirrors `assertActorIsAssignee` on the server exactly; the server stays
   * authoritative and this only avoids offering an action it would refuse.
   */
  const ownerUsesAssignment = kase.currentOwner === 'OPS' || kase.currentOwner === 'COMMERCIAL';
  const mayAct = isOwnerRole || (isCurrentOwner && (ownerUsesAssignment ? isAssignee : true));

  const canRespond = can('inspection:authorization:respond') && kase.status === 'PENDING' && mayAct;
  /** Claim takes an unclaimed case out of the department intake pool. */
  const canClaim =
    can('inspection:authorization:respond') &&
    isCurrentOwner &&
    ownerUsesAssignment &&
    kase.status === 'PENDING' &&
    isUnclaimed;
  /** Only the assignee may release their own claim; Owner may override. */
  const canUnclaim =
    can('inspection:authorization:respond') &&
    ownerUsesAssignment &&
    !isUnclaimed &&
    (isAssignee || isOwnerRole);
  const canForward =
    can('inspection:authorization:forward') &&
    mayAct &&
    kase.currentOwner === 'OPS' &&
    kase.workflowStage === 'PENDING_OPS_REVIEW';
  const canRelease =
    can('inspection:authorization:release') &&
    mayAct &&
    kase.currentOwner === 'OPS' &&
    kase.workflowStage === 'RETURNED_TO_OPS';
  const canRecordCommunication =
    can('inspection:authorization:record-communication') &&
    mayAct &&
    (kase.currentOwner === 'OPS' ||
      kase.currentOwner === 'COMMERCIAL' ||
      kase.currentOwner === 'ADMIN');
  /**
   * Commercial "Return to OPS" — mirrors the server exactly and adds nothing:
   *   permission   `inspection:authorization:forward` (the endpoint's guard)
   *   owner class  `currentOwner = COMMERCIAL` (assertActorOwnsCase)
   *   assignee     `mayAct` (assertActorIsAssignee; Owner wildcard)
   *   state        PENDING (a resolved case has nothing to hand back)
   * ADMIN-owned cases are deliberately NOT offered this control: Flow 3 / Admin
   * is out of scope of the frozen model (D-127 OD-9), so Admin keeps today's
   * API-only behaviour unchanged.
   */
  const canReturnToOps =
    can('inspection:authorization:forward') &&
    mayAct &&
    kase.currentOwner === 'COMMERCIAL' &&
    kase.status === 'PENDING';
  /**
   * Phase 7 Slice 5B — Commercial quotation actions (prepare / edit / attach /
   * send / revise / resend) mirror the server exactly and add nothing:
   *   permission   `inspection:authorization:record-communication` (Q-3 — reused)
   *   owner class  `currentOwner = COMMERCIAL` (assertActorOwnsCase)
   *   assignee     `mayAct` (assertActorIsAssignee; Owner wildcard)
   *   state        PENDING
   * Whether the KIND carries a quotation is the server's answer on the thread
   * (`requiresQuotation`), consumed inside the panel — never inferred here.
   */
  const canQuotationAct =
    canRecordCommunication && kase.currentOwner === 'COMMERCIAL' && kase.status === 'PENDING';
  /**
   * REQUEST_MODIFICATION recorded on the customer's behalf (design §J, D-127
   * OD-3′) is legal only on a quotation kind whose current revision is SENT —
   * the same condition the server enforces (G-1). Outside it the option is not
   * offered, so the existing decision list is unchanged for every other case.
   */
  const quotationAwaitingCustomer =
    !!quotation?.requiresQuotation && quotation.threadStatus === 'SENT';
  const decisionOptions = (Object.keys(CUSTOMER_RESPONSE_LABELS) as CustomerResponseType[]).filter(
    (d) => d !== 'REQUEST_MODIFICATION' || quotationAwaitingCustomer,
  );
  const isOpsFirstReview =
    kase.currentOwner === 'OPS' && kase.workflowStage === 'PENDING_OPS_REVIEW';
  const isDeptLeg = kase.currentOwner === 'COMMERCIAL' || kase.currentOwner === 'ADMIN';

  /**
   * The anchor list — derived from what this render actually produces, never a
   * fixed menu. Labels are shortened forms of each panel's own `<h3>` so the bar
   * stays compact while still naming the real section.
   *
   * The two conditional entries are gated on the same conditions that decide
   * whether their panel renders at all, so an anchor can never point at a
   * missing element. The right-hand status/action column is deliberately not
   * anchored: on desktop it sits beside this content rather than below it, so
   * scrolling to it would move the page for no reason.
   */
  const sections: AuthorizationSection[] = [
    ...(journey?.expected ? [{ id: SECTION_IDS.expectedRoute, label: 'Expected Route' }] : []),
    { id: SECTION_IDS.summary, label: 'Summary' },
    { id: SECTION_IDS.whyApproval, label: 'Why Approval' },
    { id: SECTION_IDS.inspection, label: 'Inspection' },
    { id: SECTION_IDS.justification, label: 'Justification' },
    { id: SECTION_IDS.evidence, label: 'Evidence' },
    { id: SECTION_IDS.findings, label: 'Findings' },
    ...(quotation?.requiresQuotation ? [{ id: SECTION_IDS.quotation, label: 'Quotation' }] : []),
    ...(id ? [{ id: SECTION_IDS.radar, label: 'Radar' }] : []),
    { id: SECTION_IDS.timeline, label: 'Timeline' },
  ];

  return (
    <div className="space-y-5">
      <button
        onClick={() => navigate(ROUTES.AUTHORIZATION_CENTER)}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="h-3 w-3" />
        Back to Authorization Center
      </button>

      <div>
        <h1 className="text-xl font-semibold text-foreground">{kase.caseNumber}</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {kase.approvalRequestTypeName ?? kase.customApprovalName ?? 'Authorization'} — Inspection{' '}
          {inspection?.inspectionNumber ?? kase.inspectionId}
        </p>
      </div>

      {/* PRESENT state, never a predicted stage — the only part of the old
          CaseProgress helper that OD-R1 permits. */}
      {kase.workflowStage === 'RETURNED_TO_ENGINEER' && (
        <div
          data-testid="case-returned-notice"
          className="flex items-center gap-2 rounded-lg bg-amber-500/10 px-4 py-2.5 text-sm font-medium text-amber-700"
        >
          <FontAwesomeIcon icon={faRotateLeft} className="h-3.5 w-3.5" />
          Returned to Engineer for Revision
        </div>
      )}

      {/* Section anchors — below the case number / returned-notice header area
          and directly above Expected Route, per the requested placement. Only
          sections that this render actually produces are listed (see
          `sections`), so every anchor resolves to a real element. */}
      <AuthorizationSectionNav sections={sections} />

      {/* Stage 5E — the governed expected-route layer. Same query key as the
          radar, so react-query serves both from ONE request and the expected and
          actual layers can never disagree (design §15). */}
      <div id={SECTION_IDS.expectedRoute} className={SECTION_SCROLL_MARGIN}>
        <ExpectedRoutePanel expected={journey?.expected} />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Complete Decision Package — left column (~65%) */}
        <div className="space-y-5 lg:col-span-2">
          <div id={SECTION_IDS.summary} className={SECTION_SCROLL_MARGIN}>
            <CaseSummaryCard
              kase={kase}
              inspection={inspection}
              engineerRole={engineerRole}
              step={step}
              finding={finding}
            />
          </div>
          <div id={SECTION_IDS.whyApproval} className={SECTION_SCROLL_MARGIN}>
            <WhyApprovalRequiredPanel kase={kase} step={step} finding={finding} />
          </div>
          <div id={SECTION_IDS.inspection} className={SECTION_SCROLL_MARGIN}>
            <InspectionContextPanel inspection={inspection} />
          </div>
          <div id={SECTION_IDS.justification} className={SECTION_SCROLL_MARGIN}>
            <EngineerJustificationPanel reason={kase.reason} />
          </div>
          <div id={SECTION_IDS.evidence} className={SECTION_SCROLL_MARGIN}>
            <EvidenceGallery selectedEvidence={kase.selectedEvidence} />
          </div>
          <div id={SECTION_IDS.findings} className={SECTION_SCROLL_MARGIN}>
            <FindingContextPanel kase={kase} finding={finding} />
          </div>

          {/* Phase 7 Slice 5B — the request-scoped quotation thread (design §J:
              "Case Detail (Commercial leg): a Quotation panel"). Renders nothing
              for kinds without a quotation; the wrapper carries the anchor only
              when the panel exists so no anchor points at an empty element. */}
          {id && quotation?.requiresQuotation && (
            <div id={SECTION_IDS.quotation} className={SECTION_SCROLL_MARGIN}>
              <QuotationPanel
                authorizationId={id}
                thread={quotation}
                isLoading={quotationLoading}
                isError={quotationError}
                onRetry={() => void refetchQuotation()}
                canAct={canQuotationAct}
                contacts={contacts}
                onChanged={invalidate}
              />
            </div>
          )}
          {/* While the thread loads nothing is shown (most kinds carry no quotation, so a
              placeholder would flash on every case). A FAILED read is surfaced honestly,
              because for a quotation kind silence would hide the whole thread. */}
          {id && !quotation && quotationError && (
            <QuotationPanel
              authorizationId={id}
              thread={undefined}
              isLoading={false}
              isError
              onRetry={() => void refetchQuotation()}
              canAct={false}
              contacts={contacts}
              onChanged={invalidate}
            />
          )}

          {/* Authorization Radar — the persisted request journey (D-112; design
              §14 places the single-case radar above the unified timeline). The
              radar summarises the journey; the timeline below remains the full
              record and is never replaced by it. */}
          {id && (
            <div id={SECTION_IDS.radar} className={SECTION_SCROLL_MARGIN}>
              <AuthorizationRadar authorizationId={id} />
            </div>
          )}

          {/* Unified Timeline */}
          <div
            id={SECTION_IDS.timeline}
            className={`rounded-xl border border-border bg-card ${SECTION_SCROLL_MARGIN}`}
          >
            <div className="border-b border-border px-5 py-3.5">
              <h3 className="text-sm font-semibold text-foreground">Timeline</h3>
            </div>
            <div className="max-h-[600px] space-y-4 overflow-y-auto px-5 py-4">
              {kase.timeline.length === 0 && (
                <p className="text-sm text-muted-foreground">No activity yet.</p>
              )}
              {kase.timeline.map((entry) => {
                const isEvidenceAdded = entry.action === 'EVIDENCE_ADDED';
                const isExpanded = expandedEvidenceEntryId === entry.id;
                return (
                  <div key={entry.id} className="flex gap-3">
                    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-muted">
                      <FontAwesomeIcon
                        icon={entry.type === 'COMMUNICATION' ? faComments : faStamp}
                        className="h-3 w-3 text-muted-foreground"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="text-sm font-medium text-foreground">
                          {entry.actorName ?? 'System'}
                        </span>
                        {entry.actorRoleName && (
                          <span className="text-xs text-muted-foreground">
                            ({entry.actorRoleName})
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {new Date(entry.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <p className="mt-0.5 text-sm text-muted-foreground">
                        {entry.type === 'HISTORY'
                          ? entry.action?.split('_').join(' ')
                          : `${COMMUNICATION_METHOD_LABELS[entry.method as AuthorizationCommunicationMethod] ?? entry.method}`}
                        {entry.decision &&
                          ` — ${CUSTOMER_RESPONSE_LABELS[entry.decision as CustomerResponseType] ?? entry.decision}`}
                      </p>
                      {entry.summary && isEvidenceAdded ? (
                        <button
                          type="button"
                          onClick={() => setExpandedEvidenceEntryId(isExpanded ? null : entry.id)}
                          className="mt-1 text-left text-sm text-primary underline decoration-dotted"
                        >
                          {entry.summary}
                        </button>
                      ) : entry.summary ? (
                        <p className="mt-1 text-sm text-foreground">{entry.summary}</p>
                      ) : null}
                      {isEvidenceAdded && isExpanded && (
                        <div className="mt-2">
                          <EvidenceGallery selectedEvidence={kase.selectedEvidence} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Status + actions — right column (~35%) */}
        <div className="space-y-5">
          <CaseStatusPanel kase={kase} />

          {!isUnclaimed && (
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="mb-1 text-sm font-semibold text-foreground">Ownership</h3>
              <p className="mb-3 text-xs text-muted-foreground">
                {isAssignee
                  ? 'Claimed by you. Only you can act on this case until you release it.'
                  : 'Claimed by another user — only its assignee can act on it.'}
              </p>
              {canUnclaim && (
                <Button
                  variant="secondary"
                  onClick={() => unclaimMutation.mutate()}
                  disabled={unclaimMutation.isPending}
                >
                  Release Claim
                </Button>
              )}
            </div>
          )}

          {canClaim && (
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="mb-1 text-sm font-semibold text-foreground">Unclaimed</h3>
              <p className="mb-3 text-xs text-muted-foreground">
                This case is in your department&rsquo;s intake pool. Claiming it assigns it to you
                and removes it from your colleagues&rsquo; Authorization Center.
              </p>
              <Button
                variant="default"
                onClick={() => claimMutation.mutate()}
                disabled={claimMutation.isPending}
              >
                Claim Case
              </Button>
            </div>
          )}

          {(canRespond || canForward || canRelease || canReturnToOps) && (
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="mb-3 text-sm font-semibold text-foreground">Decision</h3>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Comment (optional)"
                className="mb-3"
                maxLength={2000}
              />
              <div className="flex flex-wrap gap-2">
                {canRelease && (
                  <>
                    <Button
                      onClick={() => releaseMutation.mutate('APPROVED')}
                      className="min-h-[40px]"
                    >
                      <FontAwesomeIcon icon={faCheck} className="mr-2 h-3.5 w-3.5" />
                      Release — Approve
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => releaseMutation.mutate('REJECTED')}
                      className="min-h-[40px]"
                    >
                      <FontAwesomeIcon icon={faXmark} className="mr-2 h-3.5 w-3.5" />
                      Release — Reject
                    </Button>
                  </>
                )}
                {canRespond && !canRelease && (
                  <>
                    <Button
                      onClick={() => respondMutation.mutate('APPROVED')}
                      className="min-h-[40px]"
                    >
                      <FontAwesomeIcon icon={faCheck} className="mr-2 h-3.5 w-3.5" />
                      Approve
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => respondMutation.mutate('REJECTED')}
                      className="min-h-[40px]"
                    >
                      <FontAwesomeIcon icon={faXmark} className="mr-2 h-3.5 w-3.5" />
                      Reject
                    </Button>
                    {isOpsFirstReview && (
                      <Button
                        variant="secondary"
                        onClick={async () => {
                          const ok = await swalConfirm({
                            title: 'Return to Engineer?',
                            text: 'This sends the case back for revision.',
                            confirmText: 'Return',
                          });
                          if (ok) respondMutation.mutate('RETURNED');
                        }}
                        className="min-h-[40px]"
                      >
                        <FontAwesomeIcon icon={faRotateLeft} className="mr-2 h-3.5 w-3.5" />
                        Return to Engineer
                      </Button>
                    )}
                  </>
                )}
                {canForward && (
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => forwardMutation.mutate('COMMERCIAL')}
                      className="min-h-[40px]"
                    >
                      <FontAwesomeIcon icon={faShare} className="mr-2 h-3.5 w-3.5" />
                      Forward to Commercial
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => forwardMutation.mutate('ADMIN')}
                      className="min-h-[40px]"
                    >
                      <FontAwesomeIcon icon={faShare} className="mr-2 h-3.5 w-3.5" />
                      Forward to Admin
                    </Button>
                  </>
                )}
                {canReturnToOps && (
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      const ok = await swalConfirm({
                        title: 'Return to Operations?',
                        text: 'This hands the case back to the OPS intake pool without a recommendation.',
                        confirmText: 'Return to OPS',
                      });
                      if (ok) returnToOpsMutation.mutate();
                    }}
                    disabled={returnToOpsMutation.isPending}
                    className="min-h-[40px]"
                  >
                    <FontAwesomeIcon icon={faRotateLeft} className="mr-2 h-3.5 w-3.5" />
                    Return to OPS
                  </Button>
                )}
              </div>
              {isDeptLeg && canRespond && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Approving or rejecting here records your recommendation and returns the case to
                  Operations for final release.
                  {canReturnToOps && ' Return to OPS hands it back without a recommendation.'}
                </p>
              )}
            </div>
          )}

          {canRecordCommunication && (
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="mb-1 text-sm font-semibold text-foreground">Send to Customer</h3>
              <p className="mb-3 text-xs text-muted-foreground">
                Sending keeps the case with your department — only the waiting state moves to the
                customer. Select a decision to record the customer&rsquo;s response instead.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Method
                  </label>
                  <select
                    value={commMethod}
                    onChange={(e) =>
                      setCommMethod(e.target.value as AuthorizationCommunicationMethod)
                    }
                    className={SELECT_CLASS}
                  >
                    {(
                      Object.keys(COMMUNICATION_METHOD_LABELS) as AuthorizationCommunicationMethod[]
                    ).map((m) => (
                      <option key={m} value={m}>
                        {COMMUNICATION_METHOD_LABELS[m]}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Customer Contact
                  </label>
                  <select
                    value={commContactId}
                    onChange={(e) => setCommContactId(e.target.value)}
                    className={SELECT_CLASS}
                  >
                    <option value="">— Select contact —</option>
                    {contacts?.map((c) => (
                      <option key={c.id} value={c.id}>
                        {`${c.firstName} ${c.lastName}`.trim()}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Summary
                  </label>
                  <Textarea
                    value={commSummary}
                    onChange={(e) => setCommSummary(e.target.value)}
                    placeholder="What was discussed"
                    maxLength={2000}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Decision (optional)
                  </label>
                  <select
                    value={commDecision}
                    onChange={(e) => setCommDecision(e.target.value as CustomerResponseType | '')}
                    className={SELECT_CLASS}
                  >
                    <option value="">— No decision yet —</option>
                    {decisionOptions.map((d) => (
                      <option key={d} value={d}>
                        {CUSTOMER_RESPONSE_LABELS[d]}
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  onClick={() => communicationMutation.mutate()}
                  disabled={!commSummary.trim() || communicationMutation.isPending}
                  className="min-h-[40px] w-full"
                >
                  {commDecision ? 'Record Customer Response' : 'Send to Customer'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
